import { useState } from "react";
import { FileText, Loader2, CheckCircle, XCircle, AlertTriangle, Shield, Mail, Globe, BarChart3, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface AuthResult {
  domain: string;
  result: string;
  selector?: string;
}

interface DmarcRecord {
  sourceIp: string;
  count: number;
  policyEvaluated: {
    disposition: string;
    dkim: string;
    spf: string;
  };
  headerFrom: string;
  authResults: {
    dkim: AuthResult[];
    spf: AuthResult[];
  };
}

interface DmarcReport {
  reportMetadata: {
    orgName: string;
    email: string;
    reportId: string;
    dateRange: {
      begin: string;
      end: string;
    };
  };
  policyPublished: {
    domain: string;
    adkim: string;
    aspf: string;
    p: string;
    sp: string;
    pct: number;
  };
  records: DmarcRecord[];
  summary: {
    totalEmails: number;
    passedDkim: number;
    failedDkim: number;
    passedSpf: number;
    failedSpf: number;
    quarantined: number;
    rejected: number;
    delivered: number;
    uniqueSourceIps: number;
  };
}

const SAMPLE_DMARC_XML = `<?xml version="1.0" encoding="UTF-8" ?>
<feedback>
  <report_metadata>
    <org_name>google.com</org_name>
    <email>noreply-dmarc-support@google.com</email>
    <report_id>12345678901234567890</report_id>
    <date_range>
      <begin>1704067200</begin>
      <end>1704153599</end>
    </date_range>
  </report_metadata>
  <policy_published>
    <domain>example.com</domain>
    <adkim>r</adkim>
    <aspf>r</aspf>
    <p>quarantine</p>
    <sp>quarantine</sp>
    <pct>100</pct>
  </policy_published>
  <record>
    <row>
      <source_ip>209.85.220.41</source_ip>
      <count>150</count>
      <policy_evaluated>
        <disposition>none</disposition>
        <dkim>pass</dkim>
        <spf>pass</spf>
      </policy_evaluated>
    </row>
    <identifiers>
      <header_from>example.com</header_from>
    </identifiers>
    <auth_results>
      <dkim>
        <domain>example.com</domain>
        <result>pass</result>
        <selector>s1</selector>
      </dkim>
      <spf>
        <domain>example.com</domain>
        <result>pass</result>
      </spf>
    </auth_results>
  </record>
  <record>
    <row>
      <source_ip>185.70.42.15</source_ip>
      <count>25</count>
      <policy_evaluated>
        <disposition>quarantine</disposition>
        <dkim>fail</dkim>
        <spf>fail</spf>
      </policy_evaluated>
    </row>
    <identifiers>
      <header_from>example.com</header_from>
    </identifiers>
    <auth_results>
      <dkim>
        <domain>malicious.com</domain>
        <result>fail</result>
      </dkim>
      <spf>
        <domain>malicious.com</domain>
        <result>fail</result>
      </spf>
    </auth_results>
  </record>
</feedback>`;

export const DmarcReportAnalyzer = () => {
  const [xmlContent, setXmlContent] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<DmarcReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadSample = () => {
    setXmlContent(SAMPLE_DMARC_XML);
    setResult(null);
    setError(null);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setXmlContent(content);
        setResult(null);
        setError(null);
      };
      reader.readAsText(file);
    }
  };

  const analyzeReport = async () => {
    if (!xmlContent.trim()) {
      toast.error("Please paste or upload a DMARC report");
      return;
    }

    setIsAnalyzing(true);
    setResult(null);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke("parse-dmarc-report", {
        body: { xmlContent: xmlContent.trim() },
      });

      if (fnError) throw fnError;

      if (data.error) {
        setError(data.error);
      } else {
        setResult(data);
      }
    } catch (err) {
      console.error("Error parsing DMARC report:", err);
      setError("Failed to parse report. Please check the XML format.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const formatDate = (timestamp: string) => {
    if (!timestamp) return "Unknown";
    const ts = parseInt(timestamp);
    if (isNaN(ts)) return timestamp;
    return new Date(ts * 1000).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getPassRate = (passed: number, total: number) => {
    if (total === 0) return 0;
    return Math.round((passed / total) * 100);
  };

  const getPolicyLabel = (policy: string) => {
    switch (policy) {
      case "reject": return { label: "Reject", color: "text-destructive", bg: "bg-destructive/10" };
      case "quarantine": return { label: "Quarantine", color: "text-warning", bg: "bg-warning/10" };
      default: return { label: "None", color: "text-muted-foreground", bg: "bg-muted" };
    }
  };

  return (
    <div className="section-card">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <BarChart3 className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="font-display text-lg font-semibold text-foreground">
            DMARC Report Analyzer
          </h3>
          <p className="text-sm text-muted-foreground">
            Parse and visualize DMARC aggregate reports
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-foreground">
              DMARC Report XML
            </label>
            <div className="flex items-center gap-2">
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".xml,.txt"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <span className="text-xs text-primary hover:text-primary/80 flex items-center gap-1">
                  <Upload className="w-3 h-3" />
                  Upload File
                </span>
              </label>
              <span className="text-muted-foreground">|</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={loadSample}
                className="text-xs text-primary hover:text-primary/80 h-auto p-0"
              >
                Load Sample
              </Button>
            </div>
          </div>
          <Textarea
            value={xmlContent}
            onChange={(e) => {
              setXmlContent(e.target.value);
              setResult(null);
              setError(null);
            }}
            placeholder="Paste your DMARC aggregate report XML here..."
            className="min-h-[150px] font-mono text-xs resize-none"
          />
        </div>

        <Button
          onClick={analyzeReport}
          disabled={isAnalyzing || !xmlContent.trim()}
          className="w-full"
        >
          {isAnalyzing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Analyzing...
            </>
          ) : (
            <>
              <FileText className="w-4 h-4 mr-2" />
              Analyze Report
            </>
          )}
        </Button>

        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-4">
            <div className="flex items-center gap-2 text-destructive">
              <XCircle className="w-5 h-5" />
              <p className="text-sm font-medium">{error}</p>
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-4 pt-4 border-t border-border">
            {/* Report Overview */}
            <div className="bg-muted/30 rounded-xl p-4 border border-border">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-sm font-medium text-foreground">{result.reportMetadata.orgName}</p>
                  <p className="text-xs text-muted-foreground">{result.reportMetadata.email}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Report Period</p>
                  <p className="text-sm text-foreground">
                    {formatDate(result.reportMetadata.dateRange.begin)} - {formatDate(result.reportMetadata.dateRange.end)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-foreground">{result.policyPublished.domain}</span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPolicyLabel(result.policyPublished.p).bg} ${getPolicyLabel(result.policyPublished.p).color}`}>
                  Policy: {getPolicyLabel(result.policyPublished.p).label}
                </span>
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-card rounded-xl p-4 border border-border text-center">
                <Mail className="w-5 h-5 text-primary mx-auto mb-2" />
                <p className="text-2xl font-bold text-foreground">{result.summary.totalEmails.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Total Emails</p>
              </div>
              <div className="bg-card rounded-xl p-4 border border-border text-center">
                <Globe className="w-5 h-5 text-primary mx-auto mb-2" />
                <p className="text-2xl font-bold text-foreground">{result.summary.uniqueSourceIps}</p>
                <p className="text-xs text-muted-foreground">Source IPs</p>
              </div>
              <div className={`rounded-xl p-4 border text-center ${result.summary.rejected > 0 ? "bg-destructive/10 border-destructive/20" : "bg-success/10 border-success/20"}`}>
                <Shield className={`w-5 h-5 mx-auto mb-2 ${result.summary.rejected > 0 ? "text-destructive" : "text-success"}`} />
                <p className={`text-2xl font-bold ${result.summary.rejected > 0 ? "text-destructive" : "text-success"}`}>{result.summary.rejected}</p>
                <p className="text-xs text-muted-foreground">Rejected</p>
              </div>
              <div className={`rounded-xl p-4 border text-center ${result.summary.quarantined > 0 ? "bg-warning/10 border-warning/20" : "bg-success/10 border-success/20"}`}>
                <AlertTriangle className={`w-5 h-5 mx-auto mb-2 ${result.summary.quarantined > 0 ? "text-warning" : "text-success"}`} />
                <p className={`text-2xl font-bold ${result.summary.quarantined > 0 ? "text-warning" : "text-success"}`}>{result.summary.quarantined}</p>
                <p className="text-xs text-muted-foreground">Quarantined</p>
              </div>
            </div>

            {/* Authentication Results */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* DKIM */}
              <div className="bg-card rounded-xl p-4 border border-border">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-foreground">DKIM Authentication</span>
                  <span className={`text-lg font-bold ${getPassRate(result.summary.passedDkim, result.summary.totalEmails) >= 90 ? "text-success" : getPassRate(result.summary.passedDkim, result.summary.totalEmails) >= 70 ? "text-warning" : "text-destructive"}`}>
                    {getPassRate(result.summary.passedDkim, result.summary.totalEmails)}%
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2 mb-2">
                  <div
                    className={`h-2 rounded-full ${getPassRate(result.summary.passedDkim, result.summary.totalEmails) >= 90 ? "bg-success" : getPassRate(result.summary.passedDkim, result.summary.totalEmails) >= 70 ? "bg-warning" : "bg-destructive"}`}
                    style={{ width: `${getPassRate(result.summary.passedDkim, result.summary.totalEmails)}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 text-success" />
                    {result.summary.passedDkim.toLocaleString()} passed
                  </span>
                  <span className="flex items-center gap-1">
                    <XCircle className="w-3 h-3 text-destructive" />
                    {result.summary.failedDkim.toLocaleString()} failed
                  </span>
                </div>
              </div>

              {/* SPF */}
              <div className="bg-card rounded-xl p-4 border border-border">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-foreground">SPF Authentication</span>
                  <span className={`text-lg font-bold ${getPassRate(result.summary.passedSpf, result.summary.totalEmails) >= 90 ? "text-success" : getPassRate(result.summary.passedSpf, result.summary.totalEmails) >= 70 ? "text-warning" : "text-destructive"}`}>
                    {getPassRate(result.summary.passedSpf, result.summary.totalEmails)}%
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2 mb-2">
                  <div
                    className={`h-2 rounded-full ${getPassRate(result.summary.passedSpf, result.summary.totalEmails) >= 90 ? "bg-success" : getPassRate(result.summary.passedSpf, result.summary.totalEmails) >= 70 ? "bg-warning" : "bg-destructive"}`}
                    style={{ width: `${getPassRate(result.summary.passedSpf, result.summary.totalEmails)}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 text-success" />
                    {result.summary.passedSpf.toLocaleString()} passed
                  </span>
                  <span className="flex items-center gap-1">
                    <XCircle className="w-3 h-3 text-destructive" />
                    {result.summary.failedSpf.toLocaleString()} failed
                  </span>
                </div>
              </div>
            </div>

            {/* Source Records */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-2">Source IP Details ({result.records.length} sources)</h4>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {result.records.map((record, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border ${
                      record.policyEvaluated.dkim === "pass" && record.policyEvaluated.spf === "pass"
                        ? "bg-success/5 border-success/20"
                        : record.policyEvaluated.disposition === "reject"
                        ? "bg-destructive/5 border-destructive/20"
                        : "bg-warning/5 border-warning/20"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono text-sm text-foreground">{record.sourceIp}</span>
                      <span className="text-sm font-medium text-foreground">{record.count.toLocaleString()} emails</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        record.policyEvaluated.dkim === "pass" ? "bg-success/20 text-success" : "bg-destructive/20 text-destructive"
                      }`}>
                        DKIM: {record.policyEvaluated.dkim}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        record.policyEvaluated.spf === "pass" ? "bg-success/20 text-success" : "bg-destructive/20 text-destructive"
                      }`}>
                        SPF: {record.policyEvaluated.spf}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPolicyLabel(record.policyEvaluated.disposition).bg} ${getPolicyLabel(record.policyEvaluated.disposition).color}`}>
                        {getPolicyLabel(record.policyEvaluated.disposition).label}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
